/**
 * Pure decimal resolution and formatting.
 * Consumed by ssm-web4 via an adapter that injects environment/currency/schema from Redux.
 */

import { isEnglishStyleLocale } from '../dates';

import {
    DECIMAL_TYPES,
    ENVIRONMENT_DECIMAL_FIELDS,
    DEFAULT_DECIMALS,
    CURRENCY_TYPES,
} from './constants';

/**
 * Get decimals from a single environment configuration object.
 * @param {Object} envConfig - Environment config (e.g. from tblSucursales)
 * @param {string} type - Decimal type (DECIMAL_TYPES)
 * @returns {number|null}
 */
export const getEnvironmentDecimals = (envConfig, type) => {
    if (!envConfig) return null;

    const fieldName = ENVIRONMENT_DECIMAL_FIELDS[type];
    if (!fieldName) return null;

    const value = envConfig[fieldName];
    if (value === undefined || value === null || value === '') return null;

    const decimals = parseInt(String(value), 10);
    return isNaN(decimals) ? null : decimals;
};

/**
 * Best decimals across multiple environments (max).
 * @param {Array<Object>} envConfigs - Array of environment configs
 * @param {string} type - Decimal type
 * @param {number|null} currencyIsoDecimals - ISO decimals for currency (for currency types fallback)
 * @returns {number}
 */
export const getBestDecimalsFromEnvironments = (envConfigs, type, currencyIsoDecimals) => {
    if (!Array.isArray(envConfigs) || envConfigs.length === 0) {
        const defaultDecimals = DEFAULT_DECIMALS[type] ?? 2;
        const useCurrencyFallback = CURRENCY_TYPES.includes(type);
        return useCurrencyFallback ? (currencyIsoDecimals ?? defaultDecimals) : defaultDecimals;
    }

    let maxDecimals = 0;
    const defaultDecimals = DEFAULT_DECIMALS[type] ?? 2;
    const useCurrencyFallback = CURRENCY_TYPES.includes(type);

    for (const envConfig of envConfigs) {
        let envDecimals = getEnvironmentDecimals(envConfig, type);
        if (envDecimals === null) {
            envDecimals = useCurrencyFallback
                ? (currencyIsoDecimals ?? defaultDecimals)
                : defaultDecimals;
        }
        if (envDecimals > maxDecimals) maxDecimals = envDecimals;
    }

    return maxDecimals;
};

/**
 * Resolve config decimals from type and environment/currency data (pure).
 * @param {Object} options
 * @param {string} options.type - Decimal type
 * @param {Object} [options.envConfig] - Single environment config
 * @param {Array<Object>} [options.envConfigs] - Multiple environment configs (uses max decimals)
 * @param {number|null} [options.currencyIsoDecimals] - For currency types fallback
 * @returns {number}
 */
export const resolveConfigDecimals = ({ type, envConfig, envConfigs, currencyIsoDecimals }) => {
    const defaultDecimals = DEFAULT_DECIMALS[type] ?? 2;
    const useCurrencyFallback = CURRENCY_TYPES.includes(type);

    if (Array.isArray(envConfigs) && envConfigs.length > 0) {
        return getBestDecimalsFromEnvironments(envConfigs, type, currencyIsoDecimals);
    }

    if (envConfig) {
        const envDecimals = getEnvironmentDecimals(envConfig, type);
        if (envDecimals !== null) return envDecimals;
        if (useCurrencyFallback && currencyIsoDecimals != null) return currencyIsoDecimals;
        return defaultDecimals;
    }

    if (useCurrencyFallback && currencyIsoDecimals != null) return currencyIsoDecimals;
    return defaultDecimals;
};

/**
 * Normalize a value to a number (accepts string with comma as decimal).
 * @param {any} value
 * @returns {number|null}
 */
export const normalizeValue = (value) => {
    if (value === null || value === undefined || value === '') return null;

    if (typeof value === 'number') {
        return isNaN(value) ? null : value;
    }

    if (typeof value === 'string') {
        const normalized = value.replace(',', '.');
        const num = parseFloat(normalized);
        return isNaN(num) ? null : num;
    }

    return null;
};

/**
 * Round a normalized decimal value to a fixed number of decimals.
 * @param {number|string} value - Will be normalized
 * @param {number} decimals
 * @returns {number|null}
 */
export const roundDecimal = (value, decimals) => {
    const num = normalizeValue(value);
    if (num === null) return null;

    const safeDecimals = Number.isInteger(decimals) && decimals >= 0 ? decimals : 0;
    const factor = 10 ** safeDecimals;
    const epsilon = num >= 0 ? Number.EPSILON : -Number.EPSILON;

    return Math.round((num + epsilon) * factor) / factor;
};

/**
 * Format a number with decimal places and locale separators (pure).
 * @param {number} value
 * @param {number} decimals
 * @param {string} [locale='es-ES']
 * @returns {string}
 */
export const formatWithLocale = (value, decimals, locale = 'es-ES') => {
    const fixedValue = value.toFixed(decimals);
    if (isEnglishStyleLocale(locale)) {
        const parts = fixedValue.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return parts.join('.');
    }
    const parts = fixedValue.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return parts.join(',');
};

/**
 * Get the number of decimals for a given config (pure).
 * Call this with already-resolved envConfig/envConfigs and currencyIsoDecimals.
 *
 * @param {Object} options
 * @param {string} options.type - Decimal type
 * @param {Object} [options.envConfig] - Single environment config
 * @param {Array<Object>} [options.envConfigs] - Multiple environment configs
 * @param {number|null} [options.currencyIsoDecimals] - Currency ISO decimals
 * @param {number|null} [options.fieldMaxDecimals] - Field schema max (min of this and config is used)
 * @returns {number}
 */
export const getDecimalsByConfig = ({
    type,
    envConfig,
    envConfigs,
    currencyIsoDecimals,
    fieldMaxDecimals,
}) => {
    const effectiveType =
        type && Object.values(DECIMAL_TYPES).includes(type) ? type : DECIMAL_TYPES.GENERAL;
    const configDecimals = resolveConfigDecimals({
        type: effectiveType,
        envConfig,
        envConfigs,
        currencyIsoDecimals,
    });

    if (fieldMaxDecimals != null && fieldMaxDecimals > 0) {
        return Math.min(fieldMaxDecimals, configDecimals);
    }
    return configDecimals;
};

/**
 * Format a decimal value with decimals, locale and optional currency symbol (pure).
 * @param {number|string} value - Will be normalized
 * @param {number} decimals
 * @param {string} [locale='es-ES']
 * @param {string|null} [currencySymbol]
 * @returns {string}
 */
export const formatDecimal = (value, decimals, locale = 'es-ES', currencySymbol = null) => {
    const num = normalizeValue(value);
    if (num === null) return value != null ? String(value) : '';
    const formatted = formatWithLocale(num, decimals, locale);
    if (currencySymbol) return `${formatted} ${currencySymbol}`;
    return formatted;
};
